import './FilterOption.scss';

import type { RegistryComponentProps } from '@boost-sd/components-registry';
import { registryComponent } from '@boost-sd/components-registry/registry';
import Button from '@components/Button';
import { InnerHTMLRenderer } from '@components/InnerHTMLRenderer';
import Tooltip from '@components/Tooltip';
import useOnClickOutside from '@hooks/useOnClickOutside';
import useTranslation from '@hooks/useTranslation';
import type { FilterOptionType, FilterParams, FilterSettings } from '@providers/FilterProvider';
import {
  FILTER_OPTION_DISPLAY_TYPE,
  FILTER_OPTION_SHOWMORE_TYPE,
  FILTER_OPTION_SORT_TYPE,
  FILTER_OPTION_TYPE,
} from '@providers/FilterProvider';
import type { Dict } from '@types';
import type { ModifiersType } from '@utils';
import {
  appendClassNames,
  createClsNameMap,
  formatMoney,
  formatPercentSaleLabel,
  formatPriceLabel,
  isMobileWidth,
  isSearchPage,
  mapModifiers,
  mergeModifiers,
  toItemArray,
} from '@utils';
import { isArray, orderBy } from 'lodash-es';
import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';

export const clsNameMap = createClsNameMap({
  elements: {
    label: createClsNameMap({
      modifiers: ['collapsed', 'hidden', 'inline-block'],
    }),
    title: createClsNameMap({
      modifiers: ['hidden-before', 'opening'],
    }),
    icon: createClsNameMap(),
    path: createClsNameMap(),
    'mobile-items-selected': createClsNameMap({}),
    itemwrapper: createClsNameMap({
      modifiers: [
        'scrollable',
        'full-height',
        'collapsed',
        'hidden',
        'horizontal',
        'style1',
        'style2',
        'style3',
        'style-expand',
        'right',
      ],
    }),
    'search-option': createClsNameMap({}),
    'content-inner': createClsNameMap({
      modifiers: ['scrollbar', 'viewMoreHorizontal'],
    }),
    listitem: createClsNameMap({
      modifiers: ['inline', 'list'],
    }),
    viewmorebtn: createClsNameMap({
      modifiers: ['horizontal'],
    }),
  },
  modifiers: ['horizontal', 'style1', 'style2', 'style3', 'style-expand', 'multi-level-tag'],
})('filter-option');

const isScrollableFilterOption = (option: FilterOptionType): boolean => {
  const { displayType, showMoreType, filterType } = option;

  const excludeDisplayTypes = [FILTER_OPTION_DISPLAY_TYPE.RANGE];

  if (excludeDisplayTypes.includes(displayType)) return false;

  const excludeFilterTypes = [
    FILTER_OPTION_TYPE.REVIEW_RATINGS,
    FILTER_OPTION_TYPE.STOCK,
    FILTER_OPTION_TYPE.PERCENT_SALE,
    FILTER_OPTION_TYPE.PRICE,
    FILTER_OPTION_TYPE.VARIANTS_PRICE,
  ];

  if (excludeFilterTypes.includes(filterType)) return false;

  const filterOptionHasScrollBar =
    showMoreType === undefined
      ? true
      : showMoreType === FILTER_OPTION_SHOWMORE_TYPE.SCROLLBAR ||
        showMoreType === FILTER_OPTION_SHOWMORE_TYPE.VIEWMORE_SCROLLBAR;

  return filterOptionHasScrollBar;
};

const isViewmoreFilterOption = (option: FilterOptionType) => {
  return (
    !!option.showMoreType &&
    [FILTER_OPTION_SHOWMORE_TYPE.VIEWMORE, FILTER_OPTION_SHOWMORE_TYPE.VIEWMORE_SCROLLBAR].includes(
      option.showMoreType
    )
  );
};

const isScrollBarFilterOption = (option: FilterOptionType) => {
  return (
    !option.showMoreType ||
    (!!option.showMoreType &&
      [
        FILTER_OPTION_SHOWMORE_TYPE.SCROLLBAR,
        FILTER_OPTION_SHOWMORE_TYPE.VIEWMORE_SCROLLBAR,
      ].includes(option.showMoreType))
  );
};

type ViewMoreState = {
  enable: boolean;
  isExpanded: boolean;
};

type FilterOptionState = {
  isCollapsed: boolean;
};

export type FilterOptionProps = RegistryComponentProps<
  {
    option: FilterOptionType;
    filterSettings?: FilterSettings;
    unitCurrency?: string;
    className?: string;
    noList?: boolean;
    children?:
      | React.ReactNode
      | ((renderProps: { filterOptions: FilterOptionType['values'] }) => React.ReactNode);
    labelModifiers?: { hidden: boolean };
    isFilterTreeVertical?: boolean;
    itemWrapperModifiers?: ModifiersType<(typeof clsNameMap)['itemwrapper'], true>;
    currentModifierType?: string;
    titleModifiers?: { opening: boolean };
    filterParams?: FilterParams;
    onToggle?: () => void;
    onClear?: () => void;
    onApply?: () => void;
    listItemModifiers?: ModifiersType<(typeof clsNameMap)['listitem']>;
    listItemRef?: React.ForwardedRef<HTMLUListElement>;
    customizeSorting?: (values: Array<Dict>) => Array<Dict>;
    refineByHorizontal?: FilterParams;
  },
  {
    sortOptionValues: (optionValues: Dict[]) => Dict[];
  }
>;

const FilterOption = forwardRef<HTMLDivElement, FilterOptionProps>(
  (
    {
      option,
      children,
      className,
      listItemModifiers,
      listItemRef,
      noList,
      labelModifiers,
      isFilterTreeVertical,
      itemWrapperModifiers,
      currentModifierType,
      titleModifiers,
      filterParams,
      onClear,
      onToggle,
      onApply,
      helpersRef,
      customizeSorting,
      filterSettings,
      refineByHorizontal,
      unitCurrency,
    },
    ref
  ) => {
    const { t } = useTranslation();

    const {
      collapseOnPCByDefault,
      collapseOnMobileByDefault,
      filterTreeMobileStyle,
      /* keepScrollState, */
      keepToggleState,
      /* keepTabOpenState, */
      startViewMore,
      startViewMoreH,
      sortManualValues,
      filterTreeHorizontalStyle,
      requestInstantly,
      filterLayout,
      filterPrefixParam,
      enableRenderFilterOptionItemWhenExpand,
    } = filterSettings || {};

    const filterHorizontalNotInstantly =
      !isMobileWidth() && !requestInstantly && filterLayout === 'horizontal';

    const optionRef = useRef<HTMLInputElement | null>(null);

    const itemWrapperRef = useRef<HTMLInputElement | null>(null);

    const [searchOption, setSearchOption] = useState<string>('');

    const getNumberViewMore = (option: FilterOptionType) => {
      const _viewMoreNumber = isFilterTreeVertical
        ? startViewMore || { list: 5, box: 3, swatch: 10 }
        : startViewMoreH || { list: 10, box: 20, swatch: 10 };

      switch (option.displayType) {
        case FILTER_OPTION_DISPLAY_TYPE.LIST:
          return _viewMoreNumber.list;

        case FILTER_OPTION_DISPLAY_TYPE.BOX:
          return _viewMoreNumber.box;

        case FILTER_OPTION_DISPLAY_TYPE.SWATCH:
          return _viewMoreNumber.swatch;
        default:
          return _viewMoreNumber.list;
      }
    };

    const scrollable = isScrollableFilterOption(option);

    const [viewMoreState, setViewMoreState] = useState<ViewMoreState>({
      enable: isViewmoreFilterOption(option),
      isExpanded: false,
    });

    const defaultCollapse = () => {
      const defaultCollapsedHorizontal =
        !isFilterTreeVertical && !isMobileWidth() && filterTreeHorizontalStyle !== 'style-expand';

      const isCollapsed = isMobileWidth()
        ? Boolean(option.isCollapseMobile || collapseOnMobileByDefault)
        : Boolean(option.isCollapsePC || collapseOnPCByDefault);

      return {
        isCollapsed: isCollapsed || defaultCollapsedHorizontal,
      };
    };

    const [filterOptionState, setFilterOptionState] = useState<FilterOptionState>(() =>
      defaultCollapse()
    );

    const toggleExpandedFilterOption = () => {
      onToggle?.();

      return setFilterOptionState((prevState) => ({
        ...prevState,
        isCollapsed: !prevState.isCollapsed,
      }));
    };

    const toggleViewMore = () => {
      setViewMoreState((prevState) => ({
        ...prevState,
        isExpanded: !prevState.isExpanded,
      }));
    };

    const getSelectedItemInOption = () => {
      const _currentFilterParams = filterHorizontalNotInstantly ? refineByHorizontal : filterParams;

      if (
        !_currentFilterParams ||
        !_currentFilterParams[option.filterOptionId] ||
        _currentFilterParams[option.filterOptionId]?.length === 0
      )
        return null;

      let optionItems = [];

      if (option.filterType === FILTER_OPTION_TYPE.COLLECTION) {
        const collectionValues = option.values as Dict[];

        const index = collectionValues.findIndex(
          (collectionValue) =>
            collectionValue.key === _currentFilterParams[option.filterOptionId][0]
        );
        if (index !== -1) {
          let collectionLabel = collectionValues[index].displayName;
          if (_currentFilterParams.tag) {
            collectionLabel += `: ${_currentFilterParams.tag}`;
          }
          optionItems.push(collectionLabel);
        }
      } else if (option.filterType === FILTER_OPTION_TYPE.PERCENT_SALE) {
        _currentFilterParams[option.filterOptionId]?.forEach((percentValue: string) => {
          const [from, to] = percentValue.split(':');
          optionItems.push(formatPercentSaleLabel(Number(to), Number(from)));
        });
      } else if (
        option.filterType === FILTER_OPTION_TYPE.PRICE ||
        option.filterType === FILTER_OPTION_TYPE.VARIANTS_PRICE
      ) {
        _currentFilterParams[option.filterOptionId]?.forEach((price: string) => {
          const [from, to] = price.split(':');

          optionItems.push(formatPriceLabel(to, from, unitCurrency));
        });
      } else if (option.displayType === FILTER_OPTION_DISPLAY_TYPE.RANGE) {
        _currentFilterParams[option.filterOptionId]?.forEach((rangeSlider: string) => {
          const [from, to] = rangeSlider.split(':');

          optionItems.push(`${from} - ${to}`);
        });
      } else {
        if (Array.isArray(_currentFilterParams[option.filterOptionId])) {
          _currentFilterParams[option.filterOptionId].forEach((item: string) => {
            optionItems.push(item);
          });
        } else {
          optionItems.push(_currentFilterParams[option.filterOptionId]);
        }
      }

      // Fix remove prefix PFSN-48509
      // remove \\ before special character ex: test\\: => test:
      const prefix = option.prefix ? option.prefix.replace(/\\/g, '') : '';

      optionItems = optionItems.map((item) => {
        if (!item) return '';
        return item.replace(prefix, '');
      });

      return optionItems?.toString().replaceAll(',', ', ');
    };

    const isSortValues = () => {
      const alwaySortFiltertype = [
        FILTER_OPTION_TYPE.REVIEW_RATINGS,
        FILTER_OPTION_TYPE.PERCENT_SALE,
      ];

      if (alwaySortFiltertype.includes(option.filterType)) return true;

      const excludeFilterType = [FILTER_OPTION_TYPE.STOCK];
      const excludeDisplayType = [
        FILTER_OPTION_DISPLAY_TYPE.RANGE,
        FILTER_OPTION_DISPLAY_TYPE.MULTI_LEVEL_COLLECTIONS,
      ];

      const isSortAll = option.valueType === 'all';
      const isSortManual =
        option.valueType !== 'all' && (sortManualValues || option.sortManualValues);
      const isTextRangeSlider =
        option.displayType === FILTER_OPTION_DISPLAY_TYPE.RANGE &&
        option.isNumberRangeSlider === false;

      return (
        !excludeFilterType.includes(option.filterType) &&
        (!excludeDisplayType.includes(option.displayType) || isTextRangeSlider) &&
        (isSortAll || isSortManual)
      );
    };

    const sortOptionValues = (values: Dict[]) => {
      // export props customizeSorting for sorting FilterOption
      if (customizeSorting && typeof customizeSorting === 'function') {
        return customizeSorting(values);
      }

      const sortType = option.sortType ? option.sortType : FILTER_OPTION_SORT_TYPE.KEY_ASCENDING;
      let _sortKey = sortType.split('-')[0];
      let _secondSortKey = _sortKey === 'key' ? 'doc_count' : 'key';

      const _sortBy = sortType.split('-')[1] as 'asc' | 'desc';

      if (option.filterType === FILTER_OPTION_TYPE.COLLECTION && _sortKey === 'key') {
        _sortKey = 'label';
        _secondSortKey = 'key';
      }

      // DON'T DELETE clone _lower use sort -> not change origin key
      const _lower_key = '_lower';

      values = values.map((value) => {
        if (typeof value[_sortKey] === 'number') {
          return {
            ...value,
            [`${_sortKey}${_lower_key}`]: value[_sortKey],
            [`${_secondSortKey}${_lower_key}`]: value[_secondSortKey]?.toString()?.toLowerCase(),
          };
        }
        if (value[_secondSortKey]) {
          return {
            ...value,
            [`${_sortKey}${_lower_key}`]: value[_sortKey]?.toString()?.toLowerCase(),
            [`${_secondSortKey}${_lower_key}`]: value[_secondSortKey]?.toString()?.toLowerCase(),
          };
        }

        return {
          ...value,
          [`${_sortKey}${_lower_key}`]: value[_sortKey]?.toString()?.toLowerCase(),
        };
      });

      values = orderBy(
        values,
        [`${_sortKey}${_lower_key}`, `${_secondSortKey}${_lower_key}`],
        [_sortBy, _sortBy]
      );

      return values;
    };

    const filterOptions = useMemo(() => {
      let optionsValues = option.values as Dict[];

      if (searchOption && isArray(optionsValues)) {
        let _searchKey = 'key';
        if (option.filterType === FILTER_OPTION_TYPE.COLLECTION) {
          _searchKey = 'label';
        }

        optionsValues = optionsValues.filter((optionItem: Dict) =>
          optionItem[_searchKey]
            .toString()
            ?.toLowerCase()
            ?.includes(searchOption?.trim()?.toLowerCase())
        );
      }

      if (isSortValues() && isArray(optionsValues)) {
        optionsValues = sortOptionValues(optionsValues);
      }

      // add collection All to first
      if (FILTER_OPTION_TYPE.COLLECTION && option.activeCollectionAll && isArray(optionsValues)) {
        const hasCollectionAll = optionsValues.some((x) => x.handle === 'all');

        if (!hasCollectionAll) {
          optionsValues.unshift({
            key: 0,
            label: t('collectionAll'),
            displayName: t('collectionAll'),
            doc_count: 0,
            handle: 'all',
          });
        }
      }

      if (isViewmoreFilterOption(option)) {
        if (viewMoreState.enable && optionsValues.length <= getNumberViewMore(option)) {
          setViewMoreState((preState) => ({
            ...preState,
            enable: false,
          }));
        } else if (!viewMoreState.enable && optionsValues.length > getNumberViewMore(option)) {
          setViewMoreState((preState) => ({
            ...preState,
            enable: true,
          }));
        }
      }

      if (!viewMoreState.enable || viewMoreState.isExpanded) {
        return optionsValues;
      }

      return toItemArray(optionsValues).slice(0, getNumberViewMore(option));
    }, [viewMoreState, option.values, searchOption]);

    const searchOptionEnable =
      ![
        FILTER_OPTION_TYPE.PRICE,
        FILTER_OPTION_TYPE.PERCENT_SALE,
        FILTER_OPTION_TYPE.STOCK,
      ].includes(option.filterType) &&
      ![
        FILTER_OPTION_DISPLAY_TYPE.MULTI_LEVEL_COLLECTIONS,
        FILTER_OPTION_DISPLAY_TYPE.MULTI_LEVEL_TAG,
        FILTER_OPTION_DISPLAY_TYPE.RANGE,
      ].includes(option.displayType) &&
      ((isMobileWidth() && option.showSearchBoxFilterMobile) ||
        (!isMobileWidth() && option.showSearchBoxFilterPC));

    const isFilterPrice = [FILTER_OPTION_TYPE.PRICE, FILTER_OPTION_TYPE.VARIANTS_PRICE].includes(
      option.filterType
    );

    const isShowToolTip = option.tooltip && !isMobileWidth();

    const modifierHorizontalOption = () => {
      if (isFilterTreeVertical || !filterTreeHorizontalStyle) return {};

      return {
        [filterTreeHorizontalStyle]: true,
      };
    };

    const isHorizontalStyleExpand =
      !isFilterTreeVertical && filterTreeHorizontalStyle === 'style-expand';

    const isItemWrapperRight = () => {
      if (optionRef) {
        const optionRefLeft = optionRef.current?.getBoundingClientRect().left || 0;
        const optionRefRight = window.innerWidth - optionRefLeft;
        const COLUMN_WIDTH_TWO_COLUMN = 532 + 40; // 40 is margin-right of filter option label
        const COLUMN_WIDTH_ONE_COLUMN = 240 + 40;

        if (
          filterTreeHorizontalStyle === 'style2' ||
          option.displayType === FILTER_OPTION_DISPLAY_TYPE.RANGE
        ) {
          return optionRefRight < COLUMN_WIDTH_TWO_COLUMN;
        } else if (filterTreeHorizontalStyle === 'style1') {
          return optionRefRight < COLUMN_WIDTH_ONE_COLUMN;
        }
      }

      return false;
    };

    useEffect(() => {
      // keep toggle state
      if (keepToggleState) {
        setFilterOptionState(defaultCollapse());
      }
    }, [filterParams]);

    useOnClickOutside(optionRef, () => {
      if (isFilterTreeVertical) return;

      if (
        filterParams &&
        refineByHorizontal &&
        filterTreeHorizontalStyle !== 'style-expand' &&
        (itemWrapperModifiers as Dict)?.hidden === false &&
        (filterParams[option.filterOptionId] ||
          (!filterParams[option.filterOptionId] &&
            refineByHorizontal[option.filterOptionId]?.length > 0))
      ) {
        onApply?.();
      }
    });

    const isShowClearCollection =
      FILTER_OPTION_TYPE.COLLECTION === option.filterType &&
      isSearchPage() &&
      filterParams &&
      filterParams[`${filterPrefixParam}c_collection`];

    // public functions
    useImperativeHandle(helpersRef, () => ({
      sortOptionValues,
    }));

    const renderFilterOptionItems = () => {
      const filterOptionChildren =
        typeof children === 'function' ? children({ filterOptions }) : children;
      return (
        <>
          {noList ? (
            filterOptionChildren
          ) : (
            <div
              className={mapModifiers(clsNameMap['content-inner'], {
                viewMoreHorizontal: !isFilterTreeVertical && isViewmoreFilterOption(option),
                scrollbar: isScrollBarFilterOption(option),
              })}
            >
              <ul
                ref={listItemRef}
                className={mapModifiers(clsNameMap.listitem, listItemModifiers)}
              >
                {filterOptionChildren}
              </ul>
            </div>
          )}

          {viewMoreState.enable && (
            <button
              className={mapModifiers(clsNameMap.viewmorebtn, {
                horizontal: !isFilterTreeVertical,
              })}
              onClick={toggleViewMore}
            >
              {viewMoreState.isExpanded ? t('viewLess') : t('viewMore')}
            </button>
          )}

          {!isFilterTreeVertical &&
            FILTER_OPTION_TYPE.COLLECTION !== option.filterType &&
            !requestInstantly &&
            filterTreeHorizontalStyle !== 'style-expand' && (
              <Button
                buttonModifiers={['apply', 'btn-filter-option-color']}
                onClick={() => onApply?.()}
              >
                {t('apply')}
              </Button>
            )}

          {!isFilterTreeVertical &&
            (getSelectedItemInOption() || isShowClearCollection) &&
            filterTreeHorizontalStyle !== 'style-expand' && (
              <Button
                aria-label={`Clear filter by ${option.label}`}
                buttonModifiers={
                  isShowClearCollection
                    ? ['clear', 'clear-collection', 'btn-filter-option-color']
                    : ['clear', 'btn-filter-option-color']
                }
                onClick={() => onClear?.()}
              >
                {t('clear')}
              </Button>
            )}
        </>
      );
    };

    return (
      <div
        className={`${appendClassNames(clsNameMap.root(), className)} ${mapModifiers(clsNameMap, {
          horizontal: !isFilterTreeVertical,
          'multi-level-tag': option.filterType === FILTER_OPTION_TYPE.MULTI_LEVEL_TAG,
          ...modifierHorizontalOption(),
        })}`}
        ref={optionRef}
      >
        <div
          className={mergeModifiers(clsNameMap['label'], [
            {
              collapsed: isFilterTreeVertical ? filterOptionState.isCollapsed : false,
              'inline-block': !isFilterTreeVertical,
            },
            labelModifiers,
          ])}
          onClick={() => onToggle?.()}
        >
          <button
            aria-label={`Filter by: ${option.label}`}
            onClick={toggleExpandedFilterOption}
            className={mapModifiers(clsNameMap.title, {
              'hidden-before': isHorizontalStyleExpand,
              ...titleModifiers,
            })}
          >
            {option.label}
            {isFilterPrice && (
              <InnerHTMLRenderer as='span' html={` (${formatMoney('', unitCurrency)})`} />
            )}

            {isShowToolTip && (
              <Tooltip content={option.tooltip || ''} hasArrow modifiers={['filter-tree']} />
            )}

            <svg
              className={clsNameMap.elm('icon')}
              xmlns='http://www.w3.org/2000/svg'
              width='7'
              height='12'
              role='img'
              viewBox='0 0 7 12'
              fill='none'
            >
              <path
                className={mapModifiers(clsNameMap.path)}
                d='M6.58942 0.610713C6.26398 0.285274 5.73635 0.285274 5.41091 0.610713L0.611415 5.41021C0.455135 5.56649 0.366829 5.77895 0.366829 5.99996C0.366829 6.22098 0.454629 6.43294 0.610909 6.58922L5.41091 11.3892C5.73635 11.7147 6.26398 11.7147 6.58942 11.3892C6.91486 11.0638 6.91486 10.5362 6.58942 10.2107L2.37867 5.99996L6.58942 1.78922C6.91486 1.46379 6.91486 0.936153 6.58942 0.610713Z'
              />
            </svg>
          </button>
          {isFilterTreeVertical &&
            (!isMobileWidth() || (isMobileWidth() && filterTreeMobileStyle === 'style1')) &&
            getSelectedItemInOption() && (
              <Button
                aria-label={`Clear filter by ${option.label}`}
                buttonModifiers={['clear']}
                onClick={() => onClear?.()}
              >
                {t('clear')}
              </Button>
            )}

          {isMobileWidth() && (
            <div className={clsNameMap.elm('mobile-items-selected')}>
              {getSelectedItemInOption()}
            </div>
          )}
        </div>
        <div
          className={mergeModifiers(clsNameMap.itemwrapper, [
            {
              scrollable,
              collapsed:
                currentModifierType === 'collapsed' ? filterOptionState.isCollapsed : false,
              right: !isHorizontalStyleExpand && isItemWrapperRight(),
            },
            itemWrapperModifiers,
          ])}
          ref={itemWrapperRef}
        >
          {searchOptionEnable && !isHorizontalStyleExpand && (
            <input
              className={clsNameMap.elm('search-option')}
              name='search'
              type='text'
              autoComplete='off'
              placeholder={t('searchOptions')}
              value={searchOption}
              onChange={(e) => setSearchOption(e.currentTarget.value)}
            />
          )}

          {filterOptionState.isCollapsed && enableRenderFilterOptionItemWhenExpand
            ? null
            : renderFilterOptionItems()}
        </div>
      </div>
    );
  }
);

export default registryComponent('FilterOption', FilterOption);
